import React, { useState, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    ArrowLeftIcon,
    TicketIcon,
    EyeIcon,
    EllipsisVerticalIcon,
    LinkIcon,
} from '@heroicons/react/20/solid'
import { Button } from '../../components/button'
import { Input, InputGroup } from '../../components/input'
import { Select } from '../../components/select'
import { Badge } from '../../components/badge'
import { Divider } from '../../components/divider'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/table'
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from '../../components/dropdown'
import { ErrorDialog, SuccessDialog, ConfirmDialog } from '../../components/custom/ResponseDialog'
import {
    Pagination,
    PaginationList,
    PaginationNext,
    PaginationPage,
    PaginationPrevious,
} from '../../components/pagination'
import { getPageRange } from '../../utils/paginationHelper'
import loadingImage from '../../assets/images/boat-loading.gif'
import { formatDateOnly } from 'utils/common'
import { UserIcon } from '@heroicons/react/16/solid'
import { GET_REFERRAL_SYSTEM_PLAN } from 'graphql/referralSystem/queries'
import { ACTIVATE_REFERRAL_PLAN } from 'graphql/referralSystem/mutations'

const ReferralConfig = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [searchString, setSearchString] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [categoryFilter, setCategoryFilter] = useState('')
    const [activateDialog, setActivateDialog] = useState({ open: false, id: null, name: '' })
    const [deactivateDialog, setDeactivateDialog] = useState({ open: false, id: null, name: '' })
    const [successDialog, setSuccessDialog] = useState({ open: false, message: '' })
    const [errorDialog, setErrorDialog] = useState({ open: false, message: '' })
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)

    // Pagination state
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    const { data, loading, error, refetch } = useQuery(GET_REFERRAL_SYSTEM_PLAN, {
        variables: {
            filter: {
            },
            page: {
                offset: page * rowsPerPage,
                limit: rowsPerPage,
            },
        },
        fetchPolicy: 'network-only',
    })

    // Update discount Status
    const [updateDiscountStatus, { loading: activateLoading }] = useMutation(ACTIVATE_REFERRAL_PLAN, {
        onCompleted: data => {
            if (data?.updateReferralSystemStatus?.success) {
                setSuccessDialog({
                    open: true,
                    message: data.updateReferralSystemStatus.message || 'referral plan activated successfully',
                })
                setShowSucDialog(true)
                refetch()
            } else {
                setErrorDialog({
                    open: true,
                    message: data?.updateReferralSystemStatus?.message || 'Failed to activate referral plan',
                })
                setShowErrDialog(true)
            }
            setActivateDialog({ open: false, id: null })
            setDeactivateDialog({ open: false, id: null })
        },
        onError: error => {
            setErrorDialog({
                open: true,
                message: error.message || 'An error occurred while activating referral plan',
            })
            setShowErrDialog(true)
            setActivateDialog({ open: false, id: null })
            setDeactivateDialog({ open: false, id: null })
        },
    })

    const referral = data?.getReferralSystem?.referral_systems || []
    const totalCount = data?.getReferralSystem?.total_count || 0
    const totalPages = Math.ceil(totalCount / rowsPerPage)

    const filteredDiscounts = referral.filter(dis => {

        const matchesStatus = !statusFilter || dis.discount_status === statusFilter

        const matchCategory = !categoryFilter || dis.route_type === categoryFilter

        return matchesStatus && matchCategory
    })

    const onClickSearch = () => {
        setPage(0)
    }

    const onClickSearchClear = () => {
        setSearchString('')
        setStatusFilter('')
        setCategoryFilter('')
        setPage(0)
    }

    const handlePageChange = newPage => {
        setPage(newPage)
    }

    const getStatusBadge = discountStatus => {
        const statusMap = {
            ACTIVE: { color: 'emerald', label: 'Active' },
            INACTIVE: { color: 'rose', label: 'Inactive' },
        }
        const status = statusMap[discountStatus] || { color: 'zinc', label: discountStatus }
        return <Badge color={status.color}>{t(status.label)}</Badge>
    }

    const getCategoryBadge = categoryCode => {
        const categoryMap = {
            DMC: { color: 'blue', label: 'Domestic' },
            INL: { color: 'purple', label: 'International' },
        }
        const category = categoryMap[categoryCode] || { color: 'zinc', label: categoryCode }
        return <Badge color={category.color}>{t(category.label)}</Badge>
    }

    const handleDialogClose = () => {
        setShowSucDialog(false)
        setSuccessDialog({ open: false, message: '' })
        setShowErrDialog(false)
        setErrorDialog({ open: false, message: '' })
    }

    const handleActivate = (id) => {
        setActivateDialog({
            open: true,
            id,
        })
    }

    const handleDeactivate = (id) => {
        setDeactivateDialog({
            open: true,
            id,
        })
    }

    const confirmActivate = () => {
        if (activateDialog.id) {
            updateDiscountStatus({
                variables: {
                    id: activateDialog.id,
                    discount_status: 'ACTIVE',
                },
            })
        }
    }

    const confirmDeactivate = () => {
        if (deactivateDialog.id) {
            updateDiscountStatus({
                variables: {
                    id: deactivateDialog.id,
                    discount_status: 'INACTIVE',
                },
            })
        }
    }
    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                        <LinkIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                    </div>
                                    <div>
                                        <span className="ms-3">{t('Manage Referral Config Plans')}</span>
                                        <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                            {t('Easily view, add, and edit referral plans for your customers.')}
                                        </p>
                                    </div>
                                </h1>
                            </div>
                        </div>
                    </div>
                    <div className="mt-4 mb-4 flex flex-col sm:flex-row flex-wrap gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('Status')}</label>
                            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                                <option value="">{t('All Statuses')}</option>
                                <option value="ACTIVE">{t('Active')}</option>
                                <option value="DRAFT">{t('Draft')}</option>
                                <option value="EXPIRED">{t('Expired')}</option>
                                <option value="INACTIVE">{t('Inactive')}</option>
                            </Select>
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('Category')}</label>
                            <Select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                                <option value="">{t('All Categories')}</option>
                                <option value="DMC">{t('Domestic')}</option>
                                <option value="INL">{t('International')}</option>
                            </Select>
                        </div>
                        <div className="mt-8 mb-4 flex flex-col sm:flex-row flex-wrap max-w-xl gap-4">
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={onClickSearchClear}
                                type="button"
                                className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                            <button
                                onClick={() => navigate('/referral-config')}
                                type="button"
                                className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Available Discounts ')} : {data?.getReferralSystem?.total_count}
                            </button>
                        </div>
                    </div>
                    </div>
                </div>
            </div>
            <Divider />
            {loading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {error && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch referrals!')}</div>
                </div>
            )}

            {!loading && !error && filteredDiscounts.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!loading && !error && filteredDiscounts.length > 0 && (
                <>
                    <div className="table-custom rounded-b-lg bg-white p-0 flex">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)] w-full">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('Valid From')}</TableHeader>
                                    <TableHeader>{t('Valid To')}</TableHeader>
                                    <TableHeader>{t('Category')}</TableHeader>
                                    <TableHeader>{t('Discount Amount')}</TableHeader>
                                    <TableHeader>{t('Status')}</TableHeader>
                                    <TableHeader>{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredDiscounts.map(discount => (
                                    <TableRow
                                        key={discount.id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => navigate(`/referral-config/edit/${discount.id}`)}
                                        title={`discount #${discount.id}`}
                                    >
                                        <TableCell className="min-w-[100px]">
                                            {formatDateOnly(discount.validity?.valid_from)}
                                        </TableCell>
                                        <TableCell className="min-w-[100px]">
                                            {formatDateOnly(discount.validity?.valid_to)}
                                        </TableCell>
                                        <TableCell className="min-w-[100px]">
                                            {getCategoryBadge(discount.route_type)}
                                        </TableCell>
                                        <TableCell className="min-w-[100px]">
                                            <span className="font-semibold text-primary">
                                                {discount.config.discount.type === 'PERCENTAGE'
                                                    ? `${discount.config.discount.value}%`
                                                    : discount.config.discount.value}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            {getStatusBadge(discount.discount_status)}
                                        </TableCell>
                                        <TableCell>
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    onClick={e => e.stopPropagation()}
                                                    className="cursor-pointer"
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/referral-config/edit/${discount.id}`)
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/referral-config/edit/${discount.id}`)
                                                        }}
                                                    >
                                                        <PencilIcon />
                                                        <DropdownLabel>{t('Edit')}</DropdownLabel>
                                                    </DropdownItem>
                                                    {discount.discount_status !== 'ACTIVE' && (
                                                        <>
                                                            <DropdownDivider />
                                                            <DropdownItem
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    handleActivate(
                                                                        discount.id,
                                                                    )
                                                                }}
                                                            >
                                                                <PlusIcon />
                                                                <DropdownLabel>{t('Activate')}</DropdownLabel>
                                                            </DropdownItem>
                                                        </>
                                                    )}
                                                    {discount.discount_status === 'ACTIVE' && (
                                                        <>
                                                            <DropdownDivider />
                                                            <DropdownItem
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    handleDeactivate(
                                                                        discount.id,
                                                                    )
                                                                }}
                                                            >
                                                                <TrashIcon />
                                                                <DropdownLabel>{t('Deactivate')}</DropdownLabel>
                                                            </DropdownItem>
                                                        </>
                                                    )}
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    {totalPages > 1 && (
                        <Pagination className="py-6">
                            <PaginationPrevious
                                onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                                isDisabled={page === 0}
                            />
                            <PaginationList>
                                {getPageRange(page, totalPages).map(index => (
                                    <PaginationPage
                                        key={index}
                                        current={index === page}
                                        onClick={() => handlePageChange(index)}
                                    >
                                        {index + 1}
                                    </PaginationPage>
                                ))}
                            </PaginationList>
                            <PaginationNext
                                onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                                isDisabled={page === totalPages - 1}
                            />
                        </Pagination>
                    )}
                    <Divider />
                </>
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={errorDialog.message}
                    onClose={handleDialogClose}
                />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={successDialog.message}
                    onClose={handleDialogClose}
                />
            )}
            {activateDialog.open && (
                <ConfirmDialog
                    status={true}
                    title={t('Activate Influencer Plan')}
                    message={`${t('Are you sure you want to activate')} ${activateDialog.id}?`}
                    onPositive={confirmActivate}
                    onNegative={() => setActivateDialog({ open: false, id: null })}
                    positiveActionText={activateLoading ? t('Activating...') : t('Activate')}
                    negativeActionText={t('Cancel')}
                />
            )}
            {deactivateDialog.open && (
                <ConfirmDialog
                    status={true}
                    title={t('Deactivate Influencer Plan')}
                    message={`${t('Are you sure you want to deactivate')} ${deactivateDialog.id}?`}
                    onPositive={confirmDeactivate}
                    onNegative={() => setDeactivateDialog({ open: false, id: null })}
                    positiveActionText={activateLoading ? t('Deactivating...') : t('Deactivate')}
                    negativeActionText={t('Cancel')}
                />
            )}
        </>
    )
}

export default ReferralConfig
